import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/contexts/I18nContext';
import type { Translations } from '@/i18n/translations';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Home, Package, LogOut, User, Wallet, Bell, Search, ChevronLeft, PanelLeftClose, PanelLeft, Users, Map, Building2, BarChart3, Settings as SettingsIcon, Menu, DollarSign, Truck, Barcode, ClipboardList, ArrowRightLeft, CalendarDays, Calculator, Warehouse, FileText, Headphones, UserCheck, Route, AlertTriangle, type LucideIcon } from 'lucide-react';
import { ROUTES } from '@/lib/constants';
import { cn } from '@/lib/utils';
import { Breadcrumbs } from '@/components/common/Breadcrumbs';
import { BreadcrumbProvider } from '@/contexts/BreadcrumbContext';
import { useState, useEffect } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { api } from '@/lib/api';
import { useCarrierTour } from '@/hooks/useCarrierTour';

interface SubMenuItem {
  name: string;
  href: string;
  roles?: ('admin' | 'carrier' | 'tenant')[];
  tourId?: string;
  end?: boolean;
}

interface NavigationItem {
  id: string;
  name: string;
  href?: string;
  icon: LucideIcon;
  subItems?: SubMenuItem[];
  roles?: ('admin' | 'carrier' | 'tenant')[];
}

function buildNavigation(t: Translations): NavigationItem[] {
  return [
    {
      id: 'dashboard', name: t.nav.dashboard, icon: Home,
      subItems: [
        { name: t.nav.home, href: ROUTES.DASHBOARD, end: true },
        { name: t.nav.fcDashboard, href: '/dashboard/fc', roles: ['carrier'] },
      ],
    },
    {
      id: 'shipments', name: t.nav.shipments, icon: Package,
      subItems: [
        { name: t.nav.shipmentsList, href: ROUTES.SHIPMENTS },
        { name: t.nav.createShipment, href: ROUTES.CREATE_SHIPMENT },
      ],
    },
    {
      id: 'barcode', name: t.nav.barcode, icon: Barcode, roles: ['tenant', 'carrier'],
      subItems: [
        { name: t.nav.printBarcode, href: '/dashboard/barcode/print' },
      ],
    },
    { id: 'fulfillmentCenters', name: t.nav.fulfillmentCenters, icon: Building2, href: ROUTES.FULFILLMENT_CENTERS, roles: ['carrier'] },
    {
      id: 'manifests', name: t.nav.manifests, icon: ClipboardList, roles: ['carrier'],
      subItems: [
        { name: t.nav.manifestsAll, href: ROUTES.MANIFESTS, end: true },
        { name: t.nav.manifestsPickupRun, href: `${ROUTES.MANIFESTS}?type=pickup_run` },
        { name: t.nav.manifestsDeliveryRun, href: `${ROUTES.MANIFESTS}?type=delivery_run` },
        { name: t.nav.manifestsTransferRun, href: `${ROUTES.MANIFESTS}?type=transfer_run` },
        { name: t.nav.manifestsHubInbound, href: `${ROUTES.MANIFESTS}?type=hub_inbound` },
        { name: t.nav.manifestsHubOutbound, href: `${ROUTES.MANIFESTS}?type=hub_outbound` },
        { name: t.nav.manifestsReturnPickupRun, href: `${ROUTES.MANIFESTS}?type=return_pickup_run` },
        { name: t.nav.manifestsReturnDeliveryRun, href: `${ROUTES.MANIFESTS}?type=return_delivery_run` },
      ],
    },
    { id: 'trips', name: t.nav.trips, icon: Route, href: ROUTES.TRIPS, roles: ['carrier'] },
    {
      id: 'interBranchTransfer', name: t.nav.interBranchTransfer, icon: ArrowRightLeft, roles: ['carrier'],
      subItems: [
        { name: t.nav.createTransfer, href: '/dashboard/transfer/new' },
        { name: t.nav.transferHistory, href: '/dashboard/transfer/history' },
      ],
    },
    {
      id: 'dailyOperations', name: t.nav.dailyOperations, icon: CalendarDays, roles: ['carrier'],
      subItems: [
        { name: t.nav.todayOperations, href: '/dashboard/operations/today' },
        { name: t.nav.archive, href: '/dashboard/operations/archive' },
      ],
    },
    {
      id: 'financials', name: t.nav.financials, icon: DollarSign,
      subItems: [
        { name: t.nav.invoices, href: ROUTES.FINANCE, roles: ['carrier'] },
        { name: t.nav.customerSettlements, href: ROUTES.SETTLEMENTS },
        { name: t.nav.driverCodSettlements, href: '/dashboard/driver-cod-settlements', roles: ['carrier'] },
        { name: t.nav.driverPaySettlements, href: '/dashboard/driver-pay-settlements', roles: ['carrier'] },
        { name: t.nav.outstandingFees, href: '/dashboard/outstanding-fees', roles: ['admin', 'carrier', 'tenant'] },
        { name: t.nav.refunds, href: '/dashboard/refunds', roles: ['carrier'] },
        { name: t.nav.walletDeposit, href: ROUTES.WALLET, roles: ['admin'] },
        { name: t.nav.financialReports, href: '/dashboard/finance/reports', roles: ['carrier'] },
      ],
    },
    {
      id: 'customers', name: t.nav.customers, icon: Users, roles: ['admin', 'carrier'],
      subItems: [
        { name: t.nav.customersList, href: ROUTES.TENANTS },
        { name: t.nav.addCustomer, href: `${ROUTES.TENANTS}?new=true` },
      ],
    },
    {
      id: 'deliveryAgents', name: t.nav.deliveryAgents, icon: Truck, roles: ['carrier'],
      subItems: [
        { name: t.nav.agentsList, href: ROUTES.DRIVERS, tourId: 'tour-drivers' },
        { name: t.nav.addAgent, href: `${ROUTES.DRIVERS}?new=true` },
        { name: t.nav.agentTracking, href: ROUTES.DRIVER_TRACKING },
      ],
    },
    {
      id: 'carriers', name: t.nav.carriers, icon: Building2, roles: ['admin', 'tenant'],
      subItems: [
        { name: t.nav.carriersList, href: ROUTES.CARRIERS },
        { name: t.nav.addCarrier, href: `${ROUTES.CARRIERS}?new=true`, roles: ['admin'] },
      ],
    },
    {
      id: 'accounts', name: t.nav.accounts, icon: Calculator, roles: ['admin', 'carrier'],
      subItems: [
        { name: t.nav.usersList, href: ROUTES.USERS },
        { name: t.nav.permissions, href: '/dashboard/accounts/permissions' },
      ],
    },
    { id: 'storage', name: t.nav.storage, href: '/dashboard/storage', icon: Warehouse, roles: ['carrier'] },
    {
      id: 'coreSettings', name: t.nav.coreSettings, icon: FileText,
      subItems: [
        { name: t.nav.coverage, href: '/dashboard/settings/coverage', roles: ['admin', 'carrier'], tourId: 'tour-cities' },
        { name: t.nav.deliveryPricing, href: '/dashboard/settings/pricing-routes', roles: ['carrier'], tourId: 'tour-pricing' },
        { name: t.nav.deliveryZones, href: '/dashboard/settings/delivery-zones', roles: ['carrier'], tourId: 'tour-zones' },
        { name: t.nav.routes, href: '/dashboard/settings/routes', roles: ['carrier'], tourId: 'tour-routes' },
        { name: t.nav.warehousesList, href: '/dashboard/settings/warehouses', roles: ['carrier', 'tenant'] },
      ],
    },
    { id: 'support', name: t.nav.support, href: '/dashboard/support', icon: Headphones, roles: ['tenant', 'carrier'] },
    { id: 'map', name: t.nav.map, href: ROUTES.MAP, icon: Map, roles: ['admin', 'carrier'] },
    { id: 'reports', name: t.nav.reports, href: ROUTES.REPORTS, icon: BarChart3, roles: ['carrier'] },
    { id: 'settings', name: t.nav.settings, href: ROUTES.SETTINGS, icon: SettingsIcon, roles: ['admin', 'carrier'] },
    { id: 'exceptionLogs', name: t.nav.exceptionLogs, icon: AlertTriangle, href: '/dashboard/exception-logs', roles: ['admin'] },
  ];
}

function getFilteredNavigation(role: string, t: Translations): NavigationItem[] {
  return buildNavigation(t)
    .filter(item => !item.roles || item.roles.includes(role as 'admin' | 'carrier' | 'tenant'))
    .map(item => {
      if (!item.subItems) return item;
      const filteredSubItems = item.subItems.filter(
        sub => !sub.roles || sub.roles.includes(role as 'admin' | 'carrier' | 'tenant')
      );
      if (filteredSubItems.length === 0) return null;
      return { ...item, subItems: filteredSubItems };
    })
    .filter((item): item is NavigationItem => item !== null);
}

export function DashboardLayout() {
  const { user, logout } = useAuth();
  const { lang, toggleLang, t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState<string[]>([]);
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const [carrierStatus, setCarrierStatus] = useState<string | null>(null);

  useEffect(() => {
    api.get('/api/v1/dashboard/wallet/balance').then((res: any) => {
      if (res?.data?.balance !== undefined) setWalletBalance(res.data.balance);
    }).catch(() => {});
  }, [user?.id]);

  useEffect(() => {
    if (user?.role !== 'carrier' || !user.carrier_id) return;
    api.get('/api/v1/carriers/onboarding-status').then((res: any) => {
      setCarrierStatus(res.data?.status ?? null);
    }).catch(() => {});
  }, [user?.carrier_id]);

  useCarrierTour(carrierStatus, (ids) => {
    setOpenMenus((prev) => Array.from(new Set([...prev, ...ids])));
  });

  useEffect(() => {
    const activeParentIds = getFilteredNavigation(user?.role ?? '', t)
      .filter(item => isParentActive(item))
      .map(item => item.id);
    if (activeParentIds.length > 0) {
      setOpenMenus((prev) => Array.from(new Set([...prev, ...activeParentIds])));
    }
  }, [location.pathname, location.search, user?.role, t]);

  const handleLogout = () => { logout(); navigate(ROUTES.LOGIN); };

  const toggleMenu = (menuId: string) => {
    setOpenMenus(prev =>
      prev.includes(menuId) ? prev.filter(m => m !== menuId) : [...prev, menuId]
    );
  };

  const isPathActive = (href: string) => {
    const [basePath, queryString] = href.split('?');
    const pathMatch = location.pathname === basePath || location.pathname.startsWith(basePath + '/');
    if (!pathMatch) return false;
    if (queryString) {
      const hrefType = new URLSearchParams(queryString).get('type');
      if (hrefType) {
        const currentType = new URLSearchParams(location.search).get('type');
        return currentType === hrefType;
      }
    }
    return true;
  };

  const isParentActive = (item: NavigationItem) => {
    if (item.subItems) return item.subItems.some(sub => isPathActive(sub.href));
    return item.href ? isPathActive(item.href) : false;
  };

  const NavContent = () => (
    <div className="flex flex-col h-full">
      <div className="border-b border-slate-600/60">
        <div className="flex h-16 items-center justify-start px-3">
          {sidebarCollapsed
            ? <img src="/logo-icon.svg" alt="QuBox" className="w-9 h-auto" />
            : <img src="/logo-white.svg" alt="QuBox" className="w-36 h-auto" />
          }
        </div>
        {!sidebarCollapsed && (
          <div className="px-4 pb-3">
            <div className="rounded-lg bg-slate-700/50 p-3 space-y-1.5">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-blue-400" />
                <span className="text-sm font-medium text-slate-100">{user?.carrier_name ?? 'QuBox'}</span>
              </div>
              <div className="flex items-center gap-2">
                <UserCheck className="h-4 w-4 text-slate-400" />
                <span className="text-xs text-slate-400">{user?.display_name || t.nav.systemAdmin}</span>
              </div>
            </div>
          </div>
        )}
      </div>
      <nav className="flex-1 overflow-y-auto py-2">
        <ul className="space-y-0.5 px-2">
          {getFilteredNavigation(user?.role ?? '', t).map((item) => {
            const hasSubItems = item.subItems && item.subItems.length > 0;
            const isOpen = openMenus.includes(item.id);
            const isActive = isParentActive(item);
            const Icon = item.icon;
            if (hasSubItems) {
              return (
                <li key={item.id}>
                  <Collapsible open={isOpen && !sidebarCollapsed} onOpenChange={() => toggleMenu(item.id)}>
                    <CollapsibleTrigger asChild>
                      <button className={cn(
                        'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                        isActive ? 'text-blue-400 font-medium bg-slate-700/40' : 'text-slate-300 hover:bg-slate-700/60 hover:text-white'
                      )}>
                        <ChevronLeft className={cn("h-4 w-4 text-slate-400 transition-transform", isOpen && "rotate-90")} />
                        {!sidebarCollapsed && <span className="flex-1 text-start">{item.name}</span>}
                        <Icon className="h-5 w-5 text-blue-400" />
                      </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <ul className="mt-1 space-y-0.5 me-7 border-e border-slate-600/50 pe-2">
                        {item.subItems?.map((subItem) => (
                          <li key={subItem.href}>
                            <NavLink
                              to={subItem.href} end={subItem.end} id={subItem.tourId}
                              onClick={() => isMobile && setMobileMenuOpen(false)}
                              className={({ isActive: linkActive }) => {
                                const hrefHasType = subItem.href.includes('?type=');
                                const active = hrefHasType
                                  ? isPathActive(subItem.href)
                                  : linkActive && !new URLSearchParams(location.search).get('type');
                                return cn(
                                  'block rounded-md px-3 py-1.5 text-sm transition-colors',
                                  active
                                    ? 'bg-blue-500/20 text-blue-300 font-medium'
                                    : 'text-slate-400 hover:bg-slate-700/50 hover:text-slate-100'
                                );
                              }}
                            >{subItem.name}</NavLink>
                          </li>
                        ))}
                      </ul>
                    </CollapsibleContent>
                  </Collapsible>
                </li>
              );
            }
            return (
              <li key={item.id}>
                <NavLink
                  to={item.href!} end={item.href === ROUTES.DASHBOARD}
                  onClick={() => isMobile && setMobileMenuOpen(false)}
                  className={({ isActive }) => cn(
                    'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                    isActive ? 'bg-blue-500/20 text-blue-300 font-medium' : 'text-slate-300 hover:bg-slate-700/60 hover:text-white'
                  )}
                >
                  <span className="w-4" />
                  {!sidebarCollapsed && <span className="flex-1">{item.name}</span>}
                  <Icon className="h-5 w-5 text-blue-400" />
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );

  return (
    <BreadcrumbProvider>
      <div className="flex min-h-screen w-full">
        {!isMobile && (
          <aside className={cn(
            "border-l border-slate-600/50 bg-slate-800 transition-all duration-300 flex flex-col shrink-0",
            sidebarCollapsed ? "w-16" : "w-64"
          )}>
            <NavContent />
          </aside>
        )}
        {isMobile && (
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetContent side="right" className="w-72 p-0 border-slate-600">
              <div className="flex flex-col h-full bg-slate-800"><NavContent /></div>
            </SheetContent>
          </Sheet>
        )}
        <div className="flex flex-1 flex-col order-1">
          <header className="sticky top-0 z-10 flex h-14 items-center gap-2 md:gap-4 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4 lg:px-6">
            {isMobile ? (
              <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(true)}>
                <Menu className="h-5 w-5" />
              </Button>
            ) : (
              <Button variant="ghost" size="icon" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
                {sidebarCollapsed ? <PanelLeft className="h-5 w-5 scale-x-[-1]" /> : <PanelLeftClose className="h-5 w-5 scale-x-[-1]" />}
              </Button>
            )}
            <div className="flex-1"><Breadcrumbs /></div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" className="hidden md:flex">
                <Search className="h-5 w-5" />
              </Button>
              <button
                onClick={toggleLang}
                className="inline-flex items-center justify-center rounded-md border border-input bg-background px-3 h-9 text-sm font-bold tracking-wide hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                {lang === 'ar' ? 'EN' : 'AR'}
              </button>
              <Button variant="ghost" size="icon" className="relative">
                <Bell className="h-5 w-5" />
                <Badge className="absolute -top-1 -left-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-destructive">3</Badge>
              </Button>
              <Button variant="outline" size="sm" className={`gap-2 hidden lg:flex ${walletBalance < 0 ? 'border-red-400 text-red-600' : ''}`}>
                <Wallet className="h-4 w-4" />
                <span className="font-semibold">{walletBalance < 0 ? '-' : ''}{Math.abs(walletBalance).toLocaleString()}</span>
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {user?.display_name?.charAt(0) || 'A'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden md:inline-block">{user?.display_name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>{t.auth.myAccount}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem><User className="ml-2 h-4 w-4" />{t.auth.profile}</DropdownMenuItem>
                  <DropdownMenuItem><Wallet className="ml-2 h-4 w-4" />{t.auth.wallet}</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                    <LogOut className="ml-2 h-4 w-4" />{t.auth.logout}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>
          <main className="flex-1 bg-muted/30"><Outlet /></main>
          <footer className="border-t bg-background py-3 px-6">
            <p className="text-center text-sm text-muted-foreground">© 2024 QuBox Dashboard. {t.footer.rights}.</p>
          </footer>
        </div>
      </div>
    </BreadcrumbProvider>
  );
}
